// Mirrors backend enum: EstadoFirmante
export enum EEstadoFirmante {
  Iniciado = "IN",
  EnProceso = "PE",
  Completado = "CO",
  Cancelado = "CA",
  Rechazado = "DE",
}

// Mirrors backend enum: TipoRelacionPersona [EnumAsInt]
export enum ETipoPersona {
  Titular = 1,
  Conyuge = 2,
  Codeudor = 3,
  Conyuge_del_Codeudor = 4,
  Fiador = 5,
  Padre_o_madre = 6,
  Hijo = 7,
}

export enum EEstadoSolicitudPrestamoConsumo {
  EnAnalisis = "En_Analisis",
  Aprobada = "Aprobada",
  Rechazada = "Rechazada",
  Liquidada = "Liquidada",
  Incompleta = "Incompleta",
  Desistida = "Desistida",
}

export enum ETipoCredito {
  Prendario = "Prendario",
  Personal = "Personal",
  Autoprenda = "Autoprenda",
  Consumo = "Consumo",
}

const TIPO_CREDITO_FROM_INT: Record<number, ETipoCredito> = {
  1: ETipoCredito.Prendario,
  2: ETipoCredito.Personal,
  3: ETipoCredito.Autoprenda,
  4: ETipoCredito.Consumo,
};

// Compatibilidad con endpoints en INT.
export function normalizeTipoCredito(
  value: number | string | null | undefined,
): ETipoCredito | undefined {
  if (value == null) return undefined;
  if (typeof value === "number") {
    return TIPO_CREDITO_FROM_INT[value];
  }
  return value as ETipoCredito;
}

export enum ELineaProducto {
  Blanca = "Blanca",
  Electro = "Electro",
  Celulares = "Celulares",
  Motos = "Motos",
  Movilidad = "Movilidad",
}

const LINEA_PRODUCTO_FROM_INT: Record<number, ELineaProducto> = {
  1: ELineaProducto.Blanca,
  2: ELineaProducto.Electro,
  3: ELineaProducto.Celulares,
  4: ELineaProducto.Motos,
  5: ELineaProducto.Movilidad,
};

const LINEA_PRODUCTO_TO_INT: Record<ELineaProducto, number> = {
  [ELineaProducto.Blanca]: 1,
  [ELineaProducto.Electro]: 2,
  [ELineaProducto.Celulares]: 3,
  [ELineaProducto.Motos]: 4,
  [ELineaProducto.Movilidad]: 5,
};

export function normalizeLineaProducto(
  value: number | string | null | undefined,
): ELineaProducto | undefined {
  if (value == null) return undefined;
  if (typeof value === "number") {
    return LINEA_PRODUCTO_FROM_INT[value];
  }
  if (/^\d+$/.test(value)) {
    return LINEA_PRODUCTO_FROM_INT[Number(value)];
  }
  return value as ELineaProducto;
}

export const isLineaMotos = (
  value: number | string | null | undefined,
): boolean => normalizeLineaProducto(value) === ELineaProducto.Motos;

export const isLineaMovilidad = (
  value: number | string | null | undefined,
): boolean => normalizeLineaProducto(value) === ELineaProducto.Movilidad;

export const getLineaProductoCode = (
  value: number | string | null | undefined,
): number => {
  const linea = normalizeLineaProducto(value);
  if (linea && linea in LINEA_PRODUCTO_TO_INT) {
    return LINEA_PRODUCTO_TO_INT[linea];
  }
  return LINEA_PRODUCTO_TO_INT[ELineaProducto.Blanca];
};

// Menor rank = mayor precedencia. Orden: Celulares > Electro > Motos > Movilidad > Blanca.
const LINEA_PRECEDENCE_RANK: Record<ELineaProducto, number> = {
  [ELineaProducto.Celulares]: 1,
  [ELineaProducto.Electro]: 2,
  [ELineaProducto.Motos]: 3,
  [ELineaProducto.Movilidad]: 4,
  [ELineaProducto.Blanca]: 5,
};

export const getLineaConPrecedencia = (selectedCodes: number[]): number => {
  let winner: ELineaProducto = ELineaProducto.Blanca;
  for (const code of selectedCodes) {
    const linea = LINEA_PRODUCTO_FROM_INT[code];
    if (linea && LINEA_PRECEDENCE_RANK[linea] < LINEA_PRECEDENCE_RANK[winner]) {
      winner = linea;
    }
  }
  return LINEA_PRODUCTO_TO_INT[winner];
};

export enum ETipoUsuario {
  Interno = "Interno",
  Agenciero = "Agenciero",
  ClienteFinal = "ClienteFinal",
  Productor = "Productor",
  Gestor = "Gestor",
  Vendedor = "Vendedor",
  Supervisor = "Supervisor",
}

const TIPOS_USUARIO: ReadonlySet<string> = new Set(Object.values(ETipoUsuario));

export function isTipoUsuario(value: unknown): value is ETipoUsuario {
  return typeof value === "string" && TIPOS_USUARIO.has(value);
}

export function normalizeTipoUsuario(value: unknown): ETipoUsuario | undefined {
  return isTipoUsuario(value) ? value : undefined;
}

// Mirrors backend enum: ESituacionOperacion (MG.Common.Business.Enums) [EnumAsInt]
export enum ESituacionOperacion {
  //solicitudes
  Carga_Incompleta = "Carga_Incompleta",
  Informes_Pendientes = "Informes_Pendientes",
  Rechazada = "Rechazada",
  Rechazada_Definitivamente = "Rechazada_Definitivamente",
  En_Analisis = "En_Analisis",
  No_Apto_Habilitado = "No_Apto_Habilitado",
  Aprobada = "Aprobada",
  Confeccion_Solicitada = "Confeccion_Solicitada",
  Prenda_Confeccionada = "Prenda_Confeccionada",
  Liquidacion_solicitada = "Liquidacion_solicitada",
  Desistida = "Desistida",
  Desistida_por_tiempo = "Desistida_por_tiempo",
  Liquidada = "Liquidada",
  En_Espera_Informes_Riesgo_Vehiculo = "En_Espera_Informes_Riesgo_Vehiculo",
  Con_Vehiculo_Aprobado = "Con_Vehiculo_Aprobado",

  //creditos
  Cancelado = "Cancelado",
  Cancelado_Anticipadamente = "Cancelado_Anticipadamente",
  Cancelado_Anticipadamente_Judicial = "Cancelado_Anticipadamente_Judicial",
  Siniestro_en_tramite = "Siniestro_en_tramite",
  Siniestro_pendiente_desembolso = "Siniestro_pendiente_desembolso",
  Dacion_En_Pago = "Dacion_En_Pago",
  Finalizado = "Finalizado",
  Plazo_completado = "Plazo_completado",

  //Otras entidades
  Para_ofrecer_otras_entidades = "Para_ofrecer_otras_entidades",
  Cargada_otras_entidades = "Cargada_otras_entidades",
  Rechazada_otras_entidades = "Rechazada_otras_entidades",
  Aprobada_otras_entidades = "Aprobada_otras_entidades",
  Para_prendar_otras_entidades = "Para_prendar_otras_entidades",
  En_espera_liquidacion_otras_entidades = "En_espera_liquidacion_otras_entidades",
  Liquidada_otras_entidades = "Liquidada_otras_entidades",
}

export const SITUACION_OPERACION_CODES: Readonly<
  Record<ESituacionOperacion, number>
> = {
  //solicitudes
  [ESituacionOperacion.Carga_Incompleta]: 1,
  [ESituacionOperacion.Informes_Pendientes]: 4,
  [ESituacionOperacion.Rechazada]: 6,
  [ESituacionOperacion.Rechazada_Definitivamente]: 7,
  [ESituacionOperacion.En_Analisis]: 92,
  [ESituacionOperacion.No_Apto_Habilitado]: 99,
  [ESituacionOperacion.Aprobada]: 18,
  [ESituacionOperacion.Confeccion_Solicitada]: 80,
  [ESituacionOperacion.Prenda_Confeccionada]: 16,
  [ESituacionOperacion.Liquidacion_solicitada]: 81,
  [ESituacionOperacion.Desistida]: 19,
  [ESituacionOperacion.Desistida_por_tiempo]: 20,
  [ESituacionOperacion.Liquidada]: 8,
  [ESituacionOperacion.En_Espera_Informes_Riesgo_Vehiculo]: 83,
  [ESituacionOperacion.Con_Vehiculo_Aprobado]: 84,

  //creditos
  [ESituacionOperacion.Cancelado]: 11,
  [ESituacionOperacion.Cancelado_Anticipadamente]: 13,
  [ESituacionOperacion.Cancelado_Anticipadamente_Judicial]: 25,
  [ESituacionOperacion.Siniestro_en_tramite]: 50,
  [ESituacionOperacion.Siniestro_pendiente_desembolso]: 51,
  [ESituacionOperacion.Dacion_En_Pago]: 59,
  [ESituacionOperacion.Finalizado]: 60,
  [ESituacionOperacion.Plazo_completado]: 70,

  //Otras entidades
  [ESituacionOperacion.Para_ofrecer_otras_entidades]: 61,
  [ESituacionOperacion.Cargada_otras_entidades]: 62,
  [ESituacionOperacion.Rechazada_otras_entidades]: 63,
  [ESituacionOperacion.Aprobada_otras_entidades]: 64,
  [ESituacionOperacion.Para_prendar_otras_entidades]: 65,
  [ESituacionOperacion.En_espera_liquidacion_otras_entidades]: 66,
  [ESituacionOperacion.Liquidada_otras_entidades]: 67,
};

const SITUACIONES_OPERACION: ReadonlySet<string> = new Set(
  Object.values(ESituacionOperacion),
);

export function isSituacionOperacion(
  value: unknown,
): value is ESituacionOperacion {
  return typeof value === "string" && SITUACIONES_OPERACION.has(value);
}
